import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { workspacePopoverTooltipContentClassName } from "@/layouts/WorkspaceLayout/components/workspacePopoverTooltipClassName";
import { cn } from "@/utils";
import { Image as ImageIcon } from "lucide-react";
import { useState, type ReactElement } from "react";

function CanvasHeroPreview({
  label,
  imageUrl,
  onImageError,
}: {
  label: string;
  imageUrl?: string;
  onImageError?: () => void;
}) {
  return (
    <div className="w-64">
      <div className="truncate px-0.5 pb-2 text-sm font-medium text-n-90">
        {label}
      </div>
      <div className="flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-md border border-n-10 bg-n-05">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            draggable={false}
            className="size-full object-cover"
            onError={onImageError}
          />
        ) : (
          <ImageIcon className="size-6 text-n-30" />
        )}
      </div>
    </div>
  );
}

/** Hero preview tooltip — same content as workspace tab bar previews. */
export function RailCanvasHeroPreviewTooltip({
  label,
  imageUrl,
  active,
  suppress,
  children,
}: {
  label: string;
  imageUrl?: string;
  active?: boolean;
  suppress?: boolean;
  children: ReactElement;
}) {
  const [imgErr, setImgErr] = useState(false);
  const resolvedUrl = imageUrl && !imgErr ? imageUrl : undefined;
  const showPreview = !active && !suppress;

  return (
    // grouped: shares the rail's PreviewTooltipProvider warm state — first
    // preview delayed, sliding to neighboring rows is instant (Chrome tabs).
    <Tooltip grouped open={suppress ? false : undefined}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {showPreview && (
        <TooltipContent
          side="right"
          align="start"
          // The trigger is the full-width row: 8px clears the rail scroll
          // container's px-2 to reach the rail's right edge, +4px breathing
          // room — so the preview never overlaps the row's trailing controls
          // (Open pill, kebab).
          sideOffset={12}
          collisionPadding={16}
          avoidCollisions={true}
          hideArrow
          instant
          className={cn("block p-2", workspacePopoverTooltipContentClassName)}
        >
          <CanvasHeroPreview
            label={label}
            imageUrl={resolvedUrl}
            onImageError={() => setImgErr(true)}
          />
        </TooltipContent>
      )}
    </Tooltip>
  );
}
